// Map / level select screen — a paged "campaign atlas".
//
// The campaign has 9 maps. They are shown 3-per-page across up to 3 painted
// backgrounds, each authored at 2666×1473 (cover-fit onto the 1280×720 canvas):
//   - page 0 → `ui-map`   (public/ui/map_123.png) — Greenwold / Stonebridge / Frostfell
//   - page 1 → `ui-map-2` (public/ui/map_456.png) — Whispering Woods / Dragon's Spine / Sunken Marsh
//   - page 2 → `ui-map-3` (public/ui/map_789.png) — Goldmere Coast / Ironforge Approach / Twilight Chasm
//
// Each page is painted with the "frozen" (grayed/locked-looking) treatment
// baked directly into the art for however many of its trailing levels are
// still locked — e.g. `ui-map-f2` shows only the first level in color, the
// other two painted gray. Since maps unlock in strictly ascending order,
// locked levels within a page are always a trailing run, so `showPage` just
// counts how many maps on the page are locked and picks the matching
// pre-painted variant (see PAGE_TEXTURE_VARIANTS) — no runtime desaturation.
//
// Every painted page shares the SAME three shield positions (left/center/right,
// `SHIELD_RECTS`) and the same bottom banner rects, so a single set of
// runtime hit zones + gold hover highlights works on all pages. If a page's
// painted PNG is missing, that page falls back to procedural parchment cards
// (same gold hover feel), so the whole campaign stays reachable without art.
//
// Clicking a shield opens a small parchment popup with Squire/Knight/Lord
// buttons (gated by SaveData.campaign.cleared), reusing the same registry
// handoff to GameScene that MenuScene previously used.
//
// Progression is Squire-gated, not level-gated: each map shows 3 stars (one
// per difficulty — Squire/Knight/Lord), lit as `SaveData.campaign.cleared[key]`
// climbs from 0-3. A map unlocks once the PREVIOUS map has at least 1 star
// (cleared on Squire); the first map is always open. See `isMapLocked`.
import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { THEME, panel } from "../ui/theme";
import { CAMPAIGN_MAPS, type MapDef } from "../maps/maps";
import { isMapLocked, lockRequirement } from "../maps/progression";
import { save } from "../systems/Save";
import { ensureImages } from "../assets/lazyLoad";
import { addStars, openDifficultyPopup } from "../ui/campaignUi";

// Authoring resolution shared by every painted page (map.png, map-2.png, …).
// Authoring resolution shared by every painted page (map_123/456/789.png,
// white print-margins cropped off).
const AUTHOR_W = 2666;
const AUTHOR_H = 1473;

// The three shield hit rects (left / center / right) in authoring coords. Each
// rect spans a vertical card column (from the top arch to the bottom menu bar). Reused per page.
const SHIELD_RECTS: { x: number; y: number; w: number; h: number }[] = [
  { x: 300,  y: 115, w: 615, h: 1195 },
  { x: 1025, y: 115, w: 665, h: 1195 },
  { x: 1885, y: 115, w: 580, h: 1195 }
];

// Visual centers of the three painted level columns. These intentionally use
// the shields/artwork rather than SHIELD_RECTS, whose asymmetric hit areas
// make an otherwise equal-sized preview look off-center.
const PREVIEW_ANCHORS: { x: number; y: number }[] = [
  { x: 545,  y: 735 },
  { x: 1340, y: 735 },
  { x: 2140, y: 735 }
];

// Texture keys per page, ordered by how many trailing levels are painted
// frozen: [0 frozen, 1 frozen, 2 frozen, 3 frozen]. Missing keys → procedural
// fallback for that page (or the nearest less-frozen variant, see
// `pickPageTexture`).
const PAGE_TEXTURE_VARIANTS: readonly string[][] = [
  ["ui-map", "ui-map-f1", "ui-map-f2"],
  ["ui-map-2", "ui-map-2-f1", "ui-map-2-f2", "ui-map-2-f3"],
  ["ui-map-3", "ui-map-3-f1", "ui-map-3-f2", "ui-map-3-f3"],
];

// Atlas pages are heavy full-canvas art and intentionally NOT preloaded by
// BootScene (portal initial-download budget). MenuScene warms page 0 via
// warmAtlasPage as soon as the menu is up, and this scene prefetches the rest,
// so in practice the art is already in the texture cache when a page is
// opened. showPage still lazy-loads exactly the variant it wants via
// ensurePageTexture; while that is in flight the page shows a plain loading
// veil, and only a download that actually fails drops to the procedural
// parchment cards. Anything else would show the player two different
// level-select designs in a row.
const PAGE_TEXTURE_FILES: Readonly<Record<string, string>> = {
  "ui-map":      "ui/map_123.webp",
  "ui-map-f1":   "ui/map_123_f1.webp",
  "ui-map-f2":   "ui/map_123_f2.webp",
  "ui-map-2":    "ui/map_456.webp",
  "ui-map-2-f1": "ui/map_456_f1.webp",
  "ui-map-2-f2": "ui/map_456_f2.webp",
  "ui-map-2-f3": "ui/map_456_f3.webp",
  "ui-map-3":    "ui/map_789.webp",
  "ui-map-3-f1": "ui/map_789_f1.webp",
  "ui-map-3-f2": "ui/map_789_f2.webp",
  "ui-map-3-f3": "ui/map_789_f3.webp",
};
const MAPS_PER_PAGE = SHIELD_RECTS.length;

// A page whose art has not arrived after this long is treated as unavailable
// and falls back to the procedural cards — a request that never settles must
// not strand the atlas on its loading veil forever.
const ATLAS_TIMEOUT_MS = 8000;

/** The painted variant a campaign page needs at the current progress (locked
 *  levels are a trailing run, so the count alone picks the pre-painted frozen
 *  art), or undefined if that page has no art at all. */
function atlasPageAsset(
  pageIndex: number,
  data: ReturnType<typeof save.load>
): { key: string; url: string } | undefined {
  const variants = PAGE_TEXTURE_VARIANTS[pageIndex];
  if (!variants) return undefined;
  const maps = CAMPAIGN_MAPS.slice(pageIndex * MAPS_PER_PAGE, pageIndex * MAPS_PER_PAGE + MAPS_PER_PAGE);
  const frozenCount = maps.filter(m => isMapLocked(m, data)).length;
  const key = variants[Math.min(frozenCount, variants.length - 1)];
  const url = PAGE_TEXTURE_FILES[key];
  return url ? { key, url } : undefined;
}

/** Pull a campaign page's painted art down ahead of time. MenuScene warms
 *  page 0 once the menu is up (after the portal's initial-download window has
 *  been closed), so opening the atlas never has to show a loading state. */
export function warmAtlasPage(
  scene: Phaser.Scene,
  pageIndex: number,
  data: ReturnType<typeof save.load>
): void {
  const asset = atlasPageAsset(pageIndex, data);
  if (!asset || scene.textures.exists(asset.key)) return;
  void ensureImages(scene, [asset]);
}

// Difficulty-star row, anchored near the bottom of each level column so the
// rating stays clear of the shield, title, and map artwork (authoring coords).
const STAR_ROW_Y = 1215;
const STAR_SIZE = 70;
const STAR_GAP = 92;

// Bottom-bar cartouche rectangles (authoring coords, shared by all painted
// pages). The painted cartouches are blank; labels are drawn at runtime.
const BACK_RECT  = { x: 390,  y: 1340, w: 450, h: 90 };
const LEVEL_RECT = { x: 1020, y: 1340, w: 680, h: 90 };
const SKILL_TREE_RECT = { x: 1870, y: 1340, w: 510, h: 90 };

export class MapSelectScene extends Phaser.Scene {
  private scaleFactor = 1;
  private offsetX = 0;
  private offsetY = 0;
  private pageIndex = 0;
  private pageLayer?: Phaser.GameObjects.Container;
  /** The outgoing page during a cross-fade — kept alive under the incoming one
   *  so the menu underneath never shows through the seam. */
  private fadingOut?: Phaser.GameObjects.Container;
  private prevBtn?: Phaser.GameObjects.Container;
  private nextBtn?: Phaser.GameObjects.Container;
  private dots: Phaser.GameObjects.Arc[] = [];
  private popup?: Phaser.GameObjects.Container;
  /** Timestamp of the last page change; used to swallow the click that
   *  triggered navigation so it can't fall through onto a freshly built
   *  card/shield created under the pointer. */
  private lastPageChange = 0;
  /** Atlas variants already requested this scene run — a variant whose file
   *  is missing must not be re-requested in an endless rebuild loop. */
  private requestedAtlas = new Set<string>();
  /** Variants whose download finished without producing a texture (missing
   *  file, network error, or timed out). Those pages get the procedural cards;
   *  everything still downloading gets the loading veil instead. */
  private failedAtlas = new Set<string>();

  constructor() { super("MapSelectScene"); }

  create(): void {
    // Reset reused-instance state (Phaser keeps the same Scene class instance
    // across shutdown/start — stale references to destroyed GameObjects must
    // not leak in).
    this.popup = undefined;
    this.pageLayer = undefined;
    this.fadingOut = undefined;
    this.prevBtn = undefined;
    this.nextBtn = undefined;
    this.dots = [];
    this.pageIndex = 0;
    this.requestedAtlas = new Set<string>();
    this.failedAtlas = new Set<string>();

    // Keep the main menu underneath the atlas and leave a slim, intentional
    // glimpse of it around the book.  A camera zoom also scales hit zones and
    // labels together, so the page remains fully interactive.
    this.cameras.main.setZoom(0.86);
    this.cameras.main.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);

    // Cover-fit transform for the authoring layout (2666×1473 → canvas). All
    // painted pages share this, so shields/banners line up regardless of which
    // page's PNG is loaded (or missing).
    this.scaleFactor = Math.max(GAME_WIDTH / AUTHOR_W, GAME_HEIGHT / AUTHOR_H);
    this.offsetX = (GAME_WIDTH  - AUTHOR_W * this.scaleFactor) / 2;
    this.offsetY = (GAME_HEIGHT - AUTHOR_H * this.scaleFactor) / 2;

    const data = save.load();

    // Persistent bottom-bar actions. The close action lives in the atlas's
    // top-right corner so the large right cartouche can be useful navigation.
    this.makeRectZone(BACK_RECT, () => this.closeAtlas());
    this.makeRectZone(SKILL_TREE_RECT, () => this.openSkillTree());

    // Page navigation (arrows + dots) + keyboard.
    this.buildNav(data);
    this.buildCloseButton();

    // ESC returns to menu (or closes an open popup first).
    const onEscape = () => {
      if (this.popup) { this.closePopup(); return; }
      this.closeAtlas();
    };
    const onLeft = () => { if (!this.popup) this.goToPage(this.pageIndex - 1, data); };
    const onRight = () => { if (!this.popup) this.goToPage(this.pageIndex + 1, data); };
    this.input.keyboard?.on("keydown-ESC", onEscape);
    this.input.keyboard?.on("keydown-LEFT", onLeft);
    this.input.keyboard?.on("keydown-RIGHT", onRight);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.off("keydown-ESC", onEscape);
      this.input.keyboard?.off("keydown-LEFT", onLeft);
      this.input.keyboard?.off("keydown-RIGHT", onRight);
    });

    this.showPage(0, data);

    // Warm the pages the player has not opened yet so flipping through the
    // atlas is instant too. Delayed so page 0 (if it is still coming down)
    // keeps the bandwidth to itself first.
    this.time.delayedCall(600, () => {
      for (let i = 0; i < this.pageCount; i++) {
        if (i !== this.pageIndex) this.ensurePageTexture(i, data);
      }
    });
  }

  private get pageCount(): number {
    return Math.ceil(CAMPAIGN_MAPS.length / MAPS_PER_PAGE);
  }

  // Map authoring coords → screen coords using the shared cover-fit transform.
  private toScreen(ix: number, iy: number): { x: number; y: number } {
    return { x: ix * this.scaleFactor + this.offsetX, y: iy * this.scaleFactor + this.offsetY };
  }

  // Invisible interactive rect for the persistent bottom-bar actions.
  private makeRectZone(
    rect: { x: number; y: number; w: number; h: number },
    onClick: () => void
  ): void {
    const tl = this.toScreen(rect.x, rect.y);
    const w = rect.w * this.scaleFactor;
    const h = rect.h * this.scaleFactor;
    this.add.zone(tl.x + w / 2, tl.y + h / 2, w, h)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", onClick);
  }

  private flashLockedHint(x: number, y: number, map: MapDef): void {
    const txt = this.add.text(x, y - 80, `Locked — ${lockRequirement(map)}`, {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.blood,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    this.tweens.add({ targets: txt, alpha: 0, y: y - 110, duration: 900, onComplete: () => txt.destroy() });
  }

  // Shared entry point: honour the unlock gate, else open the difficulty popup.
  // Ignores clicks that arrive right after a page change (the navigating click
  // must not fall through onto a card/shield built under the pointer).
  private selectMap(map: MapDef, data: ReturnType<typeof save.load>, hintX: number, hintY: number): void {
    if (this.time.now - this.lastPageChange < 250) return;
    if (isMapLocked(map, data)) {
      this.flashLockedHint(hintX, hintY, map);
      return;
    }
    this.openDifficultyPopup(map);
  }

  /* --------------------------------- Paging -------------------------------- */

  private goToPage(index: number, data: ReturnType<typeof save.load>): void {
    const clamped = Phaser.Math.Clamp(index, 0, this.pageCount - 1);
    if (clamped === this.pageIndex && this.pageLayer) return;
    this.showPage(clamped, data);
  }

  private showPage(index: number, data: ReturnType<typeof save.load>): void {
    this.pageIndex = index;
    this.lastPageChange = this.time.now;

    // Cross-fade: the outgoing page is held under the incoming one until the
    // new one is opaque. A hard destroy+build would flash the menu (or, when
    // painted art finally lands, the fallback it replaces) through the seam.
    this.fadingOut?.destroy();
    const outgoing = this.pageLayer;
    this.fadingOut = outgoing;
    const layer = this.add.container(0, 0).setAlpha(outgoing ? 0 : 1);
    this.pageLayer = layer;

    const maps = CAMPAIGN_MAPS.slice(index * MAPS_PER_PAGE, index * MAPS_PER_PAGE + MAPS_PER_PAGE);
    // Locked levels are always a trailing run within a page (each map needs
    // the previous one Squire-cleared), so the count alone picks the right
    // pre-painted frozen variant.
    const frozenCount = maps.filter(m => isMapLocked(m, data)).length;
    this.ensurePageTexture(index, data);
    const tex = this.pickPageTexture(index, frozenCount);

    if (tex) {
      // Painted page: full-canvas cover-fit art (frozen levels already
      // painted gray) + shield hover hotspots.
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, tex).setOrigin(0.5);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      layer.add(img);
      maps.forEach((map, i) => {
        const rect = SHIELD_RECTS[i];
        if (rect) {
          const locked = isMapLocked(map, data);
          const cleared = data.campaign.cleared[map.key] ?? 0;
          const preview = this.addMapPreview(i, map, layer, locked);
          this.addStarRow(i, cleared, layer);
          this.makeShieldZone(rect, map, data, layer, preview);
        }
      });
      // The painted bottom-bar cartouches are blank — draw the labels.
      const label = (
        rect: { x: number; y: number; w: number; h: number },
        text: string,
        size: number,
        offsetX = 0,
        offsetY = 0
      ) => {
        const c = this.toScreen(rect.x + rect.w / 2 + offsetX, rect.y + rect.h / 2 + offsetY);
        const t = this.add.text(c.x, c.y, text, {
          fontFamily: THEME.fonts.display,
          fontSize: `${size}px`,
          color: THEME.colors.iron,
          align: "center"
        }).setOrigin(0.5);
        layer.add(t);
      };
      label(BACK_RECT, "BACK", 22);
      label(LEVEL_RECT, "LEVEL SELECT", 24);
      // The right cartouche's painted inner parchment is asymmetric and sits
      // left of its broad interaction rectangle.
      label(SKILL_TREE_RECT, `SKILL TREE  •  ${data.skillPoints}`, 17, -82);
    } else if (this.isPagePending(index, frozenCount)) {
      // Art still on the wire: hold a quiet veil rather than showing the
      // procedural cards, which would read as the page changing twice.
      this.buildLoadingPage(layer);
    } else {
      // Procedural fallback for a page whose PNG hasn't been dropped in yet.
      this.buildProceduralPage(maps, data, layer);
    }

    if (outgoing) {
      // The old page must stop answering the pointer the moment it is only a
      // backdrop, or hover highlights would fire on both layers at once.
      outgoing.list.forEach(o => o.disableInteractive());
      this.tweens.add({
        targets: layer, alpha: 1, duration: 160, ease: "Sine.Out",
        onComplete: () => {
          outgoing.destroy();
          if (this.fadingOut === outgoing) this.fadingOut = undefined;
        }
      });
    }

    this.refreshNav();
  }

  /** True while the painted art this page wants is still downloading. */
  private isPagePending(pageIndex: number, frozenCount: number): boolean {
    const variants = PAGE_TEXTURE_VARIANTS[pageIndex];
    if (!variants) return false;
    const wanted = variants[Math.min(frozenCount, variants.length - 1)];
    return this.requestedAtlas.has(wanted) && !this.failedAtlas.has(wanted);
  }

  /** Holding state for a page whose painted art is mid-download: an opaque
   *  backdrop in the atlas's own iron, so the screen reads as "the atlas,
   *  opening" instead of briefly showing a different level-select design. */
  private buildLoadingPage(layer: Phaser.GameObjects.Container): void {
    const zoom = this.cameras.main.zoom;
    layer.add(this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2,
      GAME_WIDTH / zoom + 4, GAME_HEIGHT / zoom + 4,
      THEME.hex.ironDeep, 1
    ));

    const label = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, "UNROLLING THE ATLAS", {
      fontFamily: THEME.fonts.display, fontSize: "26px", color: THEME.colors.goldDim,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5).setAlpha(0.75);
    layer.add(label);

    this.tweens.add({
      targets: label, alpha: 0.3, duration: 720, yoyo: true, repeat: -1, ease: "Sine.InOut"
    });
    label.once(Phaser.GameObjects.Events.DESTROY, () => this.tweens.killTweensOf(label));
  }

  /** Lazy-load the painted variant this page wants (atlas art is not
   *  preloaded at boot — portal download budget; MenuScene warms page 0 in the
   *  background instead). The page builds immediately with whatever
   *  pickPageTexture finds; when the download lands, the page is rebuilt once
   *  so the painted art swaps in. A missing file resolves without the texture
   *  and is never re-requested (requestedAtlas guard). Also used to prefetch
   *  the pages the player has not opened yet — the rebuild is skipped unless
   *  the page is still the one on screen. */
  private ensurePageTexture(pageIndex: number, data: ReturnType<typeof save.load>): void {
    const asset = atlasPageAsset(pageIndex, data);
    if (!asset || this.textures.exists(asset.key) || this.requestedAtlas.has(asset.key)) return;
    const key = asset.key;
    this.requestedAtlas.add(key);

    // A request that never settles would leave the veil up forever, so give
    // up after a while and fall back to the cards the player can still use.
    const bail = this.time.delayedCall(ATLAS_TIMEOUT_MS, () => {
      if (this.textures.exists(key)) return;
      this.failedAtlas.add(key);
      if (this.pageIndex === pageIndex) this.showPage(pageIndex, data);
    });

    void ensureImages(this, [asset]).then(() => {
      bail.remove();
      if (!this.textures.exists(key)) this.failedAtlas.add(key);
      if (!this.scene.isActive() || this.pageIndex !== pageIndex) return;
      this.showPage(pageIndex, data);
    });
  }

  /** Pick the pre-painted atlas variant for a page given how many of its
   *  trailing levels are locked. Falls back to the nearest less-frozen
   *  variant if the exact one failed to load, and to undefined (procedural
   *  page) if none of the page's variants are available. */
  private pickPageTexture(pageIndex: number, frozenCount: number): string | undefined {
    const variants = PAGE_TEXTURE_VARIANTS[pageIndex];
    if (!variants) return undefined;
    const wantIndex = Math.min(frozenCount, variants.length - 1);
    for (let i = wantIndex; i >= 0; i--) {
      if (this.textures.exists(variants[i])) return variants[i];
    }
    return undefined;
  }

  /** Star row for a painted atlas column, positioned just under the shield. */
  private addStarRow(slot: number, cleared: number, layer: Phaser.GameObjects.Container): void {
    const anchor = PREVIEW_ANCHORS[slot];
    if (!anchor) return;
    const center = this.toScreen(anchor.x, STAR_ROW_Y);
    addStars(this, center.x, center.y, cleared, layer, STAR_SIZE * this.scaleFactor, STAR_GAP * this.scaleFactor);
  }

  /** Overlay an exact per-map gameplay preview on the shared painted atlas.
   *  This keeps the ornate page frame while ensuring each selectable region
   *  visually promises the same biome, road geometry, and landmarks players
   *  encounter after clicking it. */
  private addMapPreview(
    slot: number,
    map: MapDef,
    layer: Phaser.GameObjects.Container,
    locked: boolean
  ): Phaser.GameObjects.Container | undefined {
    const textureKey = `map-preview-${map.key}`;
    if (!this.textures.exists(textureKey)) return;

    const anchor = PREVIEW_ANCHORS[slot];
    if (!anchor) return;

    // Equal-sized cards, centered on the actual painted shield in each column.
    const center = this.toScreen(anchor.x, anchor.y);
    const cardW = 510 * this.scaleFactor;
    const cardH = cardW * 9 / 16;
    const captionH = 38;
    const accent = locked ? THEME.hex.blood : THEME.hex.gold;

    const chrome = this.add.graphics();
    chrome.fillStyle(0x000000, 0.4);
    chrome.fillRoundedRect(-cardW / 2 - 12, -cardH / 2 - 6, cardW + 24, cardH + captionH + 20, 12);
    chrome.fillStyle(THEME.hex.ironDeep, 0.98);
    chrome.fillRoundedRect(-cardW / 2 - 7, -cardH / 2 - 7, cardW + 14, cardH + captionH + 10, 8);
    chrome.lineStyle(3, accent, 1);
    chrome.strokeRoundedRect(-cardW / 2 - 4, -cardH / 2 - 4, cardW + 8, cardH + captionH + 4, 6);

    const preview = this.add.image(0, 0, textureKey).setDisplaySize(cardW, cardH);
    if (locked) preview.setTint(0x66707a).setAlpha(0.48);

    const cardContents: Phaser.GameObjects.GameObject[] = [chrome, preview];
    if (locked) {
      const veil = this.add.rectangle(0, 0, cardW, cardH, 0x12090a, 0.52);
      const lockedTitle = this.add.text(0, -10, "LEVEL LOCKED", {
        fontFamily: THEME.fonts.display,
        fontSize: "24px",
        color: THEME.colors.blood,
        stroke: THEME.colors.ironDeep,
        strokeThickness: 5
      }).setOrigin(0.5);
      const requirement = this.add.text(0, 20, lockRequirement(map).toUpperCase(), {
        fontFamily: THEME.fonts.body,
        fontSize: "12px",
        color: THEME.colors.parchment,
        stroke: THEME.colors.ironDeep,
        strokeThickness: 3
      }).setOrigin(0.5);
      cardContents.push(veil, lockedTitle, requirement);
    }

    const description = this.add.text(
      0,
      cardH / 2 + captionH / 2,
      locked ? `${lockRequirement(map)} to enter ${map.name}` : map.description,
      {
      fontFamily: THEME.fonts.body,
      fontSize: "12px",
      color: locked ? THEME.colors.blood : THEME.colors.parchment,
      align: "center",
      wordWrap: { width: cardW - 20 }
      }
    ).setOrigin(0.5);
    cardContents.push(description);

    const previewCard = this.add.container(center.x, center.y, cardContents)
      .setAlpha(0)
      .setScale(0.96);
    layer.add(previewCard);
    return previewCard;
  }

  private buildNav(data: ReturnType<typeof save.load>): void {
    const cy = GAME_HEIGHT / 2;
    this.prevBtn = this.makeArrow(52, cy, "‹", () => this.goToPage(this.pageIndex - 1, data));
    this.nextBtn = this.makeArrow(GAME_WIDTH - 52, cy, "›", () => this.goToPage(this.pageIndex + 1, data));

    // Page dots on a dark pill, top-center, legible over any painting.
    const n = this.pageCount;
    const gap = 22;
    const totalW = (n - 1) * gap;
    const startX = GAME_WIDTH / 2 - totalW / 2;
    const y = 30;
    this.add.rectangle(GAME_WIDTH / 2, y, totalW + 44, 26, THEME.hex.ironDeep, 0.55)
      .setOrigin(0.5).setDepth(THEME.depths.hud - 1);
    this.dots = [];
    for (let i = 0; i < n; i++) {
      const dot = this.add.circle(startX + i * gap, y, 6, THEME.hex.goldDim)
        .setDepth(THEME.depths.hud)
        .setInteractive({ useHandCursor: true });
      dot.on("pointerdown", () => this.goToPage(i, data));
      this.dots.push(dot);
    }
  }

  private makeArrow(cx: number, cy: number, glyph: string, onClick: () => void): Phaser.GameObjects.Container {
    const w = 48, h = 72;
    const c = this.add.container(0, 0).setDepth(THEME.depths.hud);
    const bg = panel(this, cx - w / 2, cy - h / 2, w, h, "iron");
    const txt = this.add.text(cx, cy, glyph, {
      fontFamily: THEME.fonts.display, fontSize: "36px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5);
    c.add(bg); c.add(txt);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerover", () => { bg.setTint(0xffeec0); txt.setColor(THEME.colors.forge); });
    bg.on("pointerout",  () => { bg.clearTint(); txt.setColor(THEME.colors.gold); });
    bg.on("pointerdown", onClick);
    return c;
  }

  private refreshNav(): void {
    this.prevBtn?.setAlpha(this.pageIndex <= 0 ? 0.3 : 1);
    this.nextBtn?.setAlpha(this.pageIndex >= this.pageCount - 1 ? 0.3 : 1);
    this.dots.forEach((d, i) => {
      d.setFillStyle(i === this.pageIndex ? THEME.hex.gold : THEME.hex.goldDim);
      d.setScale(i === this.pageIndex ? 1.25 : 1);
    });
  }

  /* ----------------------------- Painted shields --------------------------- */

  // A shield hotspot on a painted page: a hidden gold highlight that fades in
  // on hover (same feel as the procedural cards), plus a click zone. Locked
  // state is already conveyed by the frozen-art page variant and the star
  // row (all empty), so this only needs the hover tint + a click-time hint.
  private makeShieldZone(
    rect: { x: number; y: number; w: number; h: number },
    map: MapDef,
    data: ReturnType<typeof save.load>,
    layer: Phaser.GameObjects.Container,
    preview?: Phaser.GameObjects.Container
  ): void {
    const tl = this.toScreen(rect.x, rect.y);
    const w = rect.w * this.scaleFactor;
    const h = rect.h * this.scaleFactor;
    const cx = tl.x + w / 2;
    const cy = tl.y + h / 2;
    const locked = isMapLocked(map, data);

    const hl = this.add.graphics().setAlpha(0);
    const tint = locked ? THEME.hex.blood : THEME.hex.gold;
    hl.fillStyle(tint, 0.08);
    hl.fillRoundedRect(tl.x, tl.y, w, h, 20);
    layer.add(hl);

    const zone = this.add.zone(cx, cy, w, h).setInteractive({ cursor: locked ? "not-allowed" : "pointer" });
    zone.on("pointerover", () => {
      this.tweens.killTweensOf(hl);
      this.tweens.add({ targets: hl, alpha: 1, duration: 140, ease: "Sine.Out" });
      if (preview) {
        this.tweens.killTweensOf(preview);
        this.tweens.add({ targets: preview, alpha: 1, scale: 1, duration: 160, ease: "Cubic.Out" });
      }
    });
    zone.on("pointerout", () => {
      this.tweens.killTweensOf(hl);
      this.tweens.add({ targets: hl, alpha: 0, duration: 130, ease: "Sine.In" });
      if (preview) {
        this.tweens.killTweensOf(preview);
        this.tweens.add({ targets: preview, alpha: 0, scale: 0.96, duration: 130, ease: "Sine.In" });
      }
    });
    zone.on("pointerdown", () => this.selectMap(map, data, cx, cy));
    layer.add(zone);
  }

  /* --------------------------- Procedural fallback ------------------------- */

  // Full procedural page for a slot whose painted PNG is not present yet. Draws
  // a parchment panel with up to three map cards (same gold hover as shields)
  // plus visible BACK / SKILL TREE / LEVEL SELECT labels.
  private buildProceduralPage(
    maps: MapDef[],
    data: ReturnType<typeof save.load>,
    layer: Phaser.GameObjects.Container
  ): void {
    // Opaque cover first: this page renders while the painted atlas art is
    // still downloading (or missing), and the main menu sits directly
    // underneath — without a backdrop both screens bleed into each other.
    // Sized for the zoomed-out camera so no menu strip peeks around the edges.
    const zoom = this.cameras.main.zoom;
    const cover = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2,
      GAME_WIDTH / zoom + 4, GAME_HEIGHT / zoom + 4,
      THEME.hex.ironDeep, 1
    );
    layer.add(cover);

    const cardW = 300, cardH = 150, gap = 28;
    const gridW = maps.length * cardW + (maps.length - 1) * gap;
    const startX = (GAME_WIDTH - gridW) / 2;
    const cardY = (GAME_HEIGHT - cardH) / 2;

    const heading = this.add.text(GAME_WIDTH / 2, cardY - 70, "CHOOSE YOUR REALM", {
      fontFamily: THEME.fonts.display, fontSize: "34px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 4
    }).setOrigin(0.5);
    layer.add(heading);

    maps.forEach((map, i) => {
      const x = startX + i * (cardW + gap);
      this.makeMapCard(map, data, x, cardY, cardW, cardH, layer);
    });

    // Visible bottom navigation labels (no painted banners here).
    const level = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 40, "LEVEL SELECT", {
      fontFamily: THEME.fonts.display, fontSize: "24px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5);
    const back = this.add.text(70, GAME_HEIGHT - 40, "BACK", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5);
    const skillTree = this.add.text(GAME_WIDTH - 110, GAME_HEIGHT - 40, `SKILL TREE  •  ${data.skillPoints}`, {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0.5);
    layer.add(level); layer.add(back); layer.add(skillTree);
  }

  // Parchment map card with gold hover, used by the procedural fallback pages.
  private makeMapCard(
    map: MapDef,
    data: ReturnType<typeof save.load>,
    x: number, y: number, w: number, h: number,
    layer: Phaser.GameObjects.Container
  ): void {
    const locked = isMapLocked(map, data);
    const cleared = data.campaign.cleared[map.key] ?? 0;

    const bbg = panel(this, x, y, w, h, "parchment");
    if (locked) bbg.setAlpha(0.6);

    const name = this.add.text(x + w / 2, y + 22, map.name, {
      fontFamily: THEME.fonts.display, fontSize: "22px",
      color: locked ? THEME.colors.lock : THEME.colors.iron
    }).setOrigin(0.5, 0);

    const desc = this.add.text(x + w / 2, y + 54, locked ? lockRequirement(map) : map.description, {
      fontFamily: THEME.fonts.body, fontSize: "14px", color: locked ? THEME.colors.blood : THEME.colors.iron,
      wordWrap: { width: w - 36 }, align: "center"
    }).setOrigin(0.5, 0);

    addStars(this, x + w / 2, y + h - 44, cleared, layer, 16, 20);

    const sub = locked ? "Locked"
      : cleared === 0 ? "Not yet cleared"
      : cleared === 1 ? "Squire cleared"
      : cleared === 2 ? "Knight cleared" : "Lord cleared";
    const subTxt = this.add.text(x + w / 2, y + h - 24, sub, {
      fontFamily: THEME.fonts.body, fontSize: "12px",
      color: locked ? THEME.colors.blood : THEME.colors.goldDim
    }).setOrigin(0.5, 0);

    bbg.setInteractive({ useHandCursor: true });
    bbg.on("pointerover", () => { bbg.setTint(0xffeec0); if (!locked) name.setColor(THEME.colors.forge); });
    bbg.on("pointerout",  () => { bbg.clearTint(); name.setColor(locked ? THEME.colors.lock : THEME.colors.iron); });
    bbg.on("pointerdown", () => this.selectMap(map, data, x + w / 2, y + h / 2));

    layer.add(bbg); layer.add(name); layer.add(desc); layer.add(subTxt);
  }

  private openDifficultyPopup(map: MapDef): void {
    if (this.popup) this.closePopup();

    const data = save.load();
    this.popup = openDifficultyPopup(this, map, {
      cleared: data.campaign.cleared[map.key] ?? 0,
      onClose: () => this.closePopup(),
      onStart: (chosen, difficulty) => {
        this.registry.set("selectedMap", chosen.key);
        this.registry.set("selectedDifficulty", difficulty);
        this.registry.set("runTutorial",
          chosen.key === "greenwold_pass" && difficulty === 1 && !save.get().tutorialCompleted);
        // MapSelect is an overlay on top of the menu, so both have to go.
        goTo(this, "GameScene", { stop: ["MenuScene"], launch: ["UIScene"] });
      },
    });
  }

  private closePopup(): void {
    this.popup?.destroy();
    this.popup = undefined;
  }

  private closeAtlas(): void {
    this.scene.stop("MapSelectScene");
  }

  private openSkillTree(): void {
    // Same here: this atlas sits over the menu, which must not survive.
    goTo(this, "SkillTreeScene", { stop: ["MenuScene"] });
  }

  private buildCloseButton(): void {
    const cx = GAME_WIDTH - 34, cy = 34, size = 42;
    const c = this.add.container(0, 0).setDepth(THEME.depths.hud + 10);
    const bg = panel(this, cx - size / 2, cy - size / 2, size, size, "iron");
    const x = this.add.text(cx, cy, "×", {
      fontFamily: THEME.fonts.display,
      fontSize: "26px",
      color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep,
      strokeThickness: 2
    }).setOrigin(0.5);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerover", () => { bg.setTint(0xffd6c9); x.setColor(THEME.colors.blood); });
    bg.on("pointerout", () => { bg.clearTint(); x.setColor(THEME.colors.parchment); });
    bg.on("pointerdown", () => this.closeAtlas());
    c.add([bg, x]);
  }
}
